import { useEffect, useRef, useState } from 'react';
import { OvertimePrompt } from './components/OvertimePrompt';
import { Header, Card, Button, Badge, useToast, Modal, Input, Textarea } from '../../shared/components';
import { CloseIcon } from '../../shared/icons';
import { getTurnoGeoRepository } from '../../shared/services/turnoGeo/repositoryFactory';
import { ValidationChecklist } from './components/ValidationChecklist';
import { DeclaredWorkMode, DECLARED_WORK_MODE_LABEL, LocationContext, LocationKind, MyDay, MyNetwork, RecordType, TurnoGeoRecord, RECORD_TYPE_LABEL } from '../../shared/services/turnoGeo/types';
import { JornadaCard } from './components/JornadaCard';
import { InfoTip } from './components/InfoTip';
import { LocationMapSheet } from './components/LocationMapSheet';
import { useLabels } from './labels';
import { deviationFrom, formatMinutes, shortDateTime, toDatetimeLocal } from './jornadaUtil';

const repo = getTurnoGeoRepository();

/** Distancia en metros entre dos coordenadas (haversine). */
function distanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const a = Math.sin(rad(lat2 - lat1) / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lon2 - lon1) / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

type GpsState = { status: 'idle' | 'requesting' | 'ok' | 'denied' | 'error'; latitude: number | null; longitude: number | null; accuracy: number | null; message?: string };

/**
 * Marcar entrada/salida - captura foto (solo si Configuracion > RequirePhoto
 * esta prendido) y GPS (si el navegador lo permite), y llama al POST de
 * marcaje. La clasificacion Presencial/Remoto/Fuera de rango la hace el
 * BACKEND (ver repository.ts del modulo turno-geo) - aqui solo se muestra el
 * resultado que devuelve.
 *
 * Decision 4 (aprobada): si el usuario NIEGA el permiso de GPS, se guarda
 * explicitamente ese hecho (locationPermissionDenied) - nunca se manda solo
 * "null" sin explicacion.
 */
export function MarcarTurnoPage() {
  const toast = useToast();
  const [requirePhoto, setRequirePhoto] = useState<boolean | null>(null);
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [gps, setGps] = useState<GpsState>({ status: 'idle', latitude: null, longitude: null, accuracy: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<TurnoGeoRecord | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [net, setNet] = useState<MyNetwork | null>(null);
  const [netError, setNetError] = useState(false);
  const [homeDialog, setHomeDialog] = useState(false);
  const [locKind, setLocKind] = useState<LocationKind | null>(null);
  const [mobileData, setMobileData] = useState(false);
  const [isRegisteringHome, setIsRegisteringHome] = useState(false);
  const [day, setDay] = useState<MyDay | null>(null);
  const [dayReceivedAt, setDayReceivedAt] = useState(0);
  const [note, setNote] = useState('');
  const [deviationAsk, setDeviationAsk] = useState<{ type: RecordType; minutes: number; target: string } | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeAt, setCloseAt] = useState('');
  const [closeNote, setCloseNote] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const labels = useLabels();
  const [ctx, setCtx] = useState<LocationContext | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [firstAsk, setFirstAsk] = useState<{ type: RecordType; confirmed: boolean } | null>(null);
  const [firstSkipped, setFirstSkipped] = useState(false);
  const [isSavingFirst, setIsSavingFirst] = useState(false);
  const [workMode, setWorkMode] = useState<DeclaredWorkMode | null>(null);
  const [workModeMissing, setWorkModeMissing] = useState(false);
  const [changingMode, setChangingMode] = useState(false);
  const workModeRef = useRef<HTMLDivElement>(null);
  const [overtimeKey, setOvertimeKey] = useState(0);

  const [officeId, setOfficeId] = useState<string | null>(null);
  const mySedes = ctx?.mySedes ?? [];
  const officeName = mySedes.find((x) => x.id === officeId)?.name ?? null;

  const loadCtx = () => repo.getMyLocationContext().then(setCtx).catch(() => setCtx(null));

  const loadDay = () =>
    repo
      .getMyDay()
      .then((d) => {
        setDay(d);
        setDayReceivedAt(performance.now());
      })
      .catch(() => {
        /* si falla se conserva lo ultimo que se mostro; el marcaje igual valida en el servidor */
      });

  // Dentro de un turno abierto se propone lo que la persona declaró en la entrada (lo puede cambiar).
  useEffect(() => {
    if (workMode || !day || day.state === 'SIN_TURNO' || day.state === 'CERRADO') return;
    const declared = day.marks.find((m) => m.type === 'ENTRADA')?.declaredWorkMode;
    if (declared) setWorkMode(declared);
  }, [day, workMode]);

  // Cada marca nueva vuelve a la vista compacta: la modalidad de la jornada se hereda sola.
  const marksCount = day?.marks.length ?? 0;
  useEffect(() => { setChangingMode(false); }, [marksCount]);
  const entryMode = day?.marks.find((m) => m.type === 'ENTRADA')?.declaredWorkMode ?? null;
  const modeInherited = !!day && day.state !== 'SIN_TURNO' && day.state !== 'CERRADO' && !!entryMode && !!workMode && !changingMode;

  // El reloj avanza solo en pantalla; se re-sincroniza con el servidor cada minuto y al volver a la app.
  useEffect(() => {
    loadDay();
    const id = setInterval(loadDay, 60_000);
    const onVisible = () => document.visibilityState === 'visible' && loadDay();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const loadNetwork = () => {
    setNetError(false);
    repo.getMyNetwork().then(setNet).catch(() => setNetError(true));
    void loadCtx();
  };

  const openRegister = () => {
    setLocKind(workMode === 'OFICINA' ? 'OFICINA' : workMode === 'REMOTO' ? 'CASA' : null);
    setMobileData(false);
    setHomeDialog(true);
  };

  async function registerHome() {
    if (!locKind) return;
    setIsRegisteringHome(true);
    try {
      const saved = await repo.registerMyHome({
        locationKind: locKind,
        saveIp: !mobileData,
        gpsLatitude: gps.latitude,
        gpsLongitude: gps.longitude,
        gpsAccuracyMeters: gps.accuracy,
      });
      const what = locKind === 'OFICINA' ? 'Oficina' : 'Lugar remoto';
      toast.show(saved.status === 'APROBADA' ? `${what} registrada y aprobada.` : `${what} registrada. Queda pendiente de aprobación del administrador.`);
      setHomeDialog(false);
      loadNetwork();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo registrar la ubicación.', 'error');
    } finally {
      setIsRegisteringHome(false);
    }
  }

  useEffect(() => {
    repo.getConfiguration().then((c) => setRequirePhoto(c.requirePhoto));
    requestGps();
    loadNetwork();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function requestGps() {
    if (!('geolocation' in navigator)) {
      setGps({ status: 'error', latitude: null, longitude: null, accuracy: null, message: 'Este dispositivo no reporta ubicación.' });
      return;
    }
    setGps((g) => ({ ...g, status: 'requesting' }));
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ status: 'ok', latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy });
      },
      (err) => {
        // Decision 4: PermissionDeniedError (code 1) se distingue explicitamente
        // de otros errores (timeout, posicion no disponible) - solo el primero
        // se guarda como "permiso negado" en el registro.
        if (err.code === err.PERMISSION_DENIED) {
          setGps({ status: 'denied', latitude: null, longitude: null, accuracy: null, message: 'Este dispositivo no tiene permiso de ubicación; para marcar desde aquí actívalo en el navegador. Si marcas sin él, se registra que el permiso estaba denegado.' });
        } else {
          setGps({ status: 'error', latitude: null, longitude: null, accuracy: null, message: 'No se pudo obtener tu ubicación (señal débil o tiempo agotado).' });
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  function onPhotoSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoDataUrl(reader.result as string);
    reader.readAsDataURL(file);
  }

  /** Entrada/salida fuera de la tolerancia del horario: se avisa y se pide confirmar (no bloquea, la desviacion queda guardada). */
  function requestMark(recordType: RecordType) {
    if (!workMode) {
      setWorkModeMissing(true);
      workModeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      toast.show('Antes de marcar, indica si estás en trabajo remoto o en la oficina.', 'error');
      return;
    }
    // Primera marcación: si aún no hay oficina ni lugar remoto registrado y el GPS sirve, se ofrece guardarlo como referencia.
    if (offerRegister && !firstSkipped) {
      setFirstAsk({ type: recordType, confirmed: false });
      return;
    }
    continueMark(recordType);
  }

  function continueMark(recordType: RecordType) {
    if (day && day.schedule.isWorkDay && (recordType === 'ENTRADA' || recordType === 'SALIDA')) {
      const target = recordType === 'ENTRADA' ? day.schedule.start : day.schedule.end;
      const minutes = deviationFrom(target, day.timeZone);
      if (Math.abs(minutes) > day.toleranceMinutes) {
        setDeviationAsk({ type: recordType, minutes, target });
        return;
      }
    }
    void submit(recordType, false);
  }

  async function submit(recordType: RecordType, deviationConfirmed: boolean) {
    if (requirePhoto && !photoDataUrl) {
      toast.show('Esta organización requiere foto para marcar. Toma una foto primero.', 'error');
      return;
    }
    setDeviationAsk(null);
    setIsSubmitting(true);
    try {
      const record = await repo.createRecord({
        recordType,
        photoDataUrl,
        detectedLatitude: gps.latitude,
        detectedLongitude: gps.longitude,
        gpsAccuracyMeters: gps.accuracy,
        locationPermissionDenied: gps.status === 'denied',
        note: note.trim() || null,
        deviceTimestamp: new Date().toISOString(),
        deviationConfirmed,
        declaredWorkMode: workMode,
        declaredOfficeNetworkId: workMode === 'OFICINA' ? officeId : null,
      });
      setLastResult(record);
      setNote('');
      loadNetwork();
      loadDay();
      if (recordType === 'SALIDA') setOvertimeKey((k) => k + 1);
      setPhotoDataUrl(null);
      toast.show(`${RECORD_TYPE_LABEL[recordType]} registrada como ${record.modalityName}.`);
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo registrar el marcaje.', 'error');
      loadDay();
    } finally {
      setIsSubmitting(false);
    }
  }

  function openCloseDialog() {
    // Sugerencia del servidor: la salida programada de ese día, ajustada para que sea posterior a la última marcación.
    // (Antes se proponía la hora de ENTRADA, que el servidor siempre rechazaba.)
    const os = day?.openShift;
    const fallback = os?.lastMarkAt ? new Date(new Date(os.lastMarkAt).getTime() + 60_000) : new Date();
    setCloseAt(toDatetimeLocal(os?.suggestedCloseAt ? new Date(os.suggestedCloseAt) : fallback));
    setCloseNote('');
    setCloseOpen(true);
  }

  async function closeOldShift() {
    if (!closeAt) {
      toast.show('Indica la hora real en que terminaste.', 'error');
      return;
    }
    if (closeAtError) {
      toast.show(closeAtError, 'error');
      return;
    }
    setIsClosing(true);
    try {
      await repo.closeOpenShift({ closeAt: new Date(closeAt).toISOString(), note: closeNote });
      toast.show('Turno anterior cerrado. Tu líder verá la nota. Ya puedes marcar.');
      setCloseOpen(false);
      loadDay();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo cerrar el turno.', 'error');
    } finally {
      setIsClosing(false);
    }
  }

  async function saveFirstReference() {
    if (!firstAsk || !workMode) return;
    const kind: LocationKind = workMode === 'OFICINA' ? 'OFICINA' : 'CASA';
    setIsSavingFirst(true);
    try {
      // Oficina: la IP es lo que la identifica (p. ej. VPN corporativa). Lugar remoto: GPS si es utilizable; si no, la IP.
      const saveIp = kind === 'OFICINA' ? !!net?.suggestedEntry : !gpsUsable;
      const saved = await repo.registerMyHome({
        locationKind: kind,
        saveIp,
        gpsLatitude: gpsUsable ? gps.latitude : null,
        gpsLongitude: gpsUsable ? gps.longitude : null,
        gpsAccuracyMeters: gpsUsable ? gps.accuracy : null,
      });
      const what = kind === 'OFICINA' ? 'Oficina' : 'Lugar remoto';
      toast.show(saved.status === 'APROBADA' ? `${what} registrada y aprobada.` : `${what} registrada. Queda pendiente de aprobación del líder.`);
      const type = firstAsk.type;
      setFirstAsk(null);
      loadNetwork();
      continueMark(type);
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar la ubicación.', 'error');
    } finally {
      setIsSavingFirst(false);
    }
  }

  // --- Mensaje de ubicación: GPS manda; se compara contra los lugares autorizados ---
  const lastGpsMark = [...(day?.marks ?? [])].reverse().find((m) => m.hasGps) ?? null;
  const gpsOk = gps.status === 'ok' && gps.latitude !== null && gps.longitude !== null;
  const gpsUsable = gpsOk && (ctx ? (gps.accuracy ?? 0) <= ctx.minGpsAccuracyMeters : true);
  // Las reglas dependen de la modalidad DECLARADA y nunca mezclan oficina con lugar remoto.
  const isOffice = workMode === 'OFICINA';
  const refKind = (k: 'sede' | 'oficina' | 'casa') => (isOffice ? k !== 'casa' : k === 'casa');
  const refPlaces = (ctx?.places ?? []).filter((p) => refKind(p.kind));
  const approved = refPlaces.filter((p) => p.kind === 'sede' || p.status === 'APROBADA');
  const own = isOffice ? net?.ownOffice ?? null : net?.home ?? null;
  // Defensivo: si la API aún no está actualizada, ctx.sedes no existe y la pantalla no debe romperse.
  const sedesCount = ctx?.sedes?.length ?? 0;
  const ipRecognized = isOffice ? !!net?.matchesOffice : !!(net?.home && net.home.matches && net.home.status === 'APROBADA');
  let nearest: { place: LocationContext['places'][number]; dist: number } | null = null;
  if (gpsOk) {
    for (const p of approved) {
      const dist = distanceMeters(gps.latitude!, gps.longitude!, p.lat, p.lon);
      if (!nearest || dist - p.radius < nearest.dist - nearest.place.radius) nearest = { place: p, dist };
    }
  }
  const inside = nearest ? nearest.dist <= nearest.place.radius : null;
  const modalidadOf = (k: 'sede' | 'oficina' | 'casa') => (k === 'casa' ? 'Remoto' : 'Oficina');
  // Estado de la parametrización para la modalidad declarada.
  const regState: 'ok' | 'sin_registro' | 'pendiente' | 'rechazada' | 'sin_coordenadas' =
    approved.length > 0 ? 'ok'
    : own?.status === 'PENDIENTE' ? 'pendiente'
    : own?.status === 'RECHAZADA' ? 'rechazada'
    : isOffice && sedesCount > 0 && !own ? 'sin_coordenadas'
    : 'sin_registro';
  let locationMessage = '';
  let locationTone: 'ok' | 'out' | 'neutral' = 'neutral';
  const vpnSede = net?.matchesOffice?.networkKind === 'VPN' ? ctx?.places.find((pl) => pl.kind === 'sede' && pl.id === net?.matchesOffice?.id) ?? null : null;
  const vpnKm = vpnSede && gpsUsable ? distanceMeters(gps.latitude!, gps.longitude!, vpnSede.lat, vpnSede.lon) / 1000 : null;
  if (!ctx || !net || !workMode) locationMessage = '';
  else if (!isOffice && net.matchesOffice?.networkKind === 'VPN' && !gpsUsable && gps.status !== 'requesting' && gps.status !== 'idle') {
    locationMessage = 'Estás conectado por la VPN de la empresa y sin GPS. El trabajo remoto se marca desde el celular con GPS; esta marcación queda registrada para auditoría de tu coordinador.';
    locationTone = 'out';
  } else if (isOffice && ipRecognized && vpnKm !== null && vpnKm > 2) {
    locationMessage = `Tu red es la VPN de ${net.matchesOffice!.name}, pero tu GPS está a ${vpnKm.toFixed(1)} km de la sede: la VPN no prueba que estés allí. Queda para auditoría.`;
    locationTone = 'out';
  } else if (ipRecognized) {
    const sede = net.matchesOffice?.name ?? 'tu lugar remoto';
    locationMessage = isOffice ? labels.t(net.matchesOffice?.networkKind === 'VPN' ? 'UBICACION_RED_VPN' : 'UBICACION_RED_OFICINA', { lugar: sede }) : 'Tu red coincide con tu lugar remoto registrado.';
    locationTone = 'ok';
  } else if (regState === 'pendiente') locationMessage = labels.t(isOffice ? 'UBICACION_OFICINA_PENDIENTE' : 'UBICACION_REMOTO_PENDIENTE');
  else if (regState === 'rechazada') locationMessage = labels.t(isOffice ? 'UBICACION_OFICINA_RECHAZADA' : 'UBICACION_REMOTO_RECHAZADA');
  else if (regState === 'sin_registro') locationMessage = labels.t(isOffice ? 'UBICACION_OFICINA_SIN_REGISTRO' : 'UBICACION_REMOTO_SIN_REGISTRO');
  else if (regState === 'sin_coordenadas') locationMessage = 'Tu red no coincide con ninguna sede registrada y las sedes no tienen coordenadas para comparar. Tu marcación queda registrada para revisión.';
  else if (gps.status === 'requesting' || gps.status === 'idle') locationMessage = '';
  else if (!gpsOk) locationMessage = labels.t('UBICACION_SIN_GPS');
  else if (!gpsUsable) locationMessage = labels.t('UBICACION_IMPRECISO', { precision: Math.round(gps.accuracy ?? 0), maximo: ctx.minGpsAccuracyMeters });
  else if (!nearest) locationMessage = labels.t('UBICACION_SIN_REFERENCIA');
  else {
    const v = { distancia: Math.round(nearest.dist), lugar: nearest.place.name, radio: Math.round(nearest.place.radius), modalidad: modalidadOf(nearest.place.kind), precision: Math.round(gps.accuracy ?? 0) };
    locationMessage = labels.t(inside ? 'UBICACION_DENTRO' : 'UBICACION_FUERA', v);
    locationTone = inside ? 'ok' : 'out';
  }
  // Se ofrece registrar este lugar cuando la modalidad declarada no tiene lugar registrado (o fue rechazado) y esta red no lo cubre.
  const canRegisterHere = isOffice ? !!net?.suggestedEntry || gpsUsable : gpsUsable || !!net?.suggestedEntry;
  const offerRegister = !!ctx && !!net && !!workMode && !ipRecognized && (regState === 'sin_registro' || regState === 'sin_coordenadas' || regState === 'rechazada') && canRegisterHere;
  const mapPlaces = (ctx?.places ?? []).map((p) => ({ kind: p.kind, name: p.name, lat: p.lat, lon: p.lon, radius: p.radius, status: p.status }));
  const mapUser = gpsOk ? { lat: gps.latitude!, lon: gps.longitude!, accuracy: gps.accuracy, inside: nearest ? inside : null } : null;

  // Validación del cierre en pantalla (el servidor vuelve a validar).
  const openShift = day?.openShift ?? null;
  const lastMarkAt = openShift?.lastMarkAt ? new Date(openShift.lastMarkAt) : null;
  const closeMin = lastMarkAt ? toDatetimeLocal(new Date(Math.floor(lastMarkAt.getTime() / 60_000) * 60_000 + 60_000)) : undefined;
  let closeAtError: string | null = null;
  if (closeAt) {
    const chosen = new Date(closeAt);
    if (Number.isNaN(chosen.getTime())) closeAtError = 'La hora indicada no es válida.';
    else if (lastMarkAt && chosen.getTime() <= lastMarkAt.getTime())
      closeAtError = `Debe ser posterior a tu última marcación del turno (${RECORD_TYPE_LABEL[openShift!.lastMarkType ?? 'ENTRADA'].toLowerCase()}: ${shortDateTime(lastMarkAt.toISOString(), day?.timeZone)}).`;
    else if (chosen.getTime() > Date.now() + 60_000) closeAtError = 'La hora de salida no puede estar en el futuro.';
  }

  const driftSeconds = lastResult?.clockDriftSeconds ?? null;
  const driftExceeded = driftSeconds !== null && day ? Math.abs(driftSeconds) > day.clockDriftMinutes * 60 : false;

  return (
    <div>
      <Header title="Marcar" subtitle="TurnoGeo" />
      <div className="space-y-4 p-4">
        {day?.openShift?.stale && (
          <div className="rounded-2xl border border-critical bg-red-50 p-4">
            <p className="text-sm font-semibold text-critical">Tienes un turno sin cerrar</p>
            <p className="mt-1 text-xs text-ink-600">
              Entraste el {shortDateTime(day.openShift.entryAt, day.timeZone)} y no marcaste la salida
              {day.openShift.blockReason === 'NUEVA_JORNADA' ? ' (ese turno es de un día anterior y ya empezó tu jornada de hoy)' : ` (más de ${day.openShift.maxShiftHours} horas)`}. Ciérralo indicando la hora real en que terminaste y una nota; tu líder la revisará. Mientras no lo cierres no puedes abrir un turno nuevo ni marcar.
            </p>
            <Button className="mt-3" variant="danger" size="md" onClick={openCloseDialog}>
              Cerrar mi turno anterior
            </Button>
          </div>
        )}

        {requirePhoto && (
          <Card>
            <p className="mb-3 text-sm font-semibold text-ink-800">Foto (obligatoria)</p>
            {photoDataUrl ? (
              <div className="relative">
                <img src={photoDataUrl} alt="Foto capturada" className="w-full rounded-xl object-cover" style={{ maxHeight: 260 }} />
                <button
                  onClick={() => setPhotoDataUrl(null)}
                  className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white"
                  aria-label="Quitar foto"
                >
                  <CloseIcon width={16} height={16} />
                </button>
              </div>
            ) : (
              <Button variant="outline" fullWidth onClick={() => fileInputRef.current?.click()}>
                Tomar foto
              </Button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onPhotoSelected} />
          </Card>
        )}
        {requirePhoto === false && <Card><p className="text-sm text-ink-500">La foto no es obligatoria para esta organización (desactivada en Configuración).</p></Card>}

        {/* Modo de trabajo declarado: obligatorio en cada marcación (queda en base de datos junto con la marcación). */}
        <div ref={workModeRef}>
          {modeInherited && workMode ? (
            <Card>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink-800">Modalidad de hoy: <Badge tone={workMode === 'OFICINA' ? 'success' : 'info'}>{workMode === 'OFICINA' && officeName ? officeName : DECLARED_WORK_MODE_LABEL[workMode]}</Badge></p>
                  <p className="mt-1 text-xs text-ink-500">Se mantiene en todas tus marcaciones de la jornada (almuerzo y salida).</p>
                </div>
                <button type="button" onClick={() => setChangingMode(true)} className="shrink-0 rounded-lg border border-ink-200 px-3 py-2 text-xs font-semibold text-brand-600 hover:bg-ink-50">Cambiar</button>
              </div>
            </Card>
          ) : (
          <Card>
            <div className="flex items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-sm font-semibold text-ink-800">
                {labels.t('MODO_TRABAJO_PREGUNTA')}
                <InfoTip title="Trabajo remoto u oficina" text={labels.t('AYUDA_MODO_TRABAJO')} />
              </p>
              {workMode ? <Badge tone={workMode === 'OFICINA' ? 'success' : 'info'}>{workMode === 'OFICINA' && officeName ? officeName : DECLARED_WORK_MODE_LABEL[workMode]}</Badge> : <Badge tone="warning">Obligatorio</Badge>}
            </div>
            <div className={`mt-3 grid gap-3 ${mySedes.length > 1 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2"}`} role="radiogroup" aria-label="Modo de trabajo">
              {(mySedes.length > 0
                ? ([{ mode: 'REMOTO' as const, id: null as string | null, name: '' }, ...mySedes.map((x) => ({ mode: 'OFICINA' as const, id: x.id as string | null, name: x.name }))])
                : ([{ mode: 'REMOTO' as const, id: null as string | null, name: '' }, { mode: 'OFICINA' as const, id: null as string | null, name: '' }])
              ).map(({ mode, id, name }) => {
                const selected = workMode === mode && (mode === 'REMOTO' || mySedes.length === 0 || officeId === id || (officeId === null && mySedes.length === 1));
                return (
                  <button
                    key={`${mode}-${id ?? ''}`}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => {
                      setWorkMode(mode);
                      setOfficeId(mode === 'OFICINA' ? id : null);
                      setWorkModeMissing(false);
                    }}
                    className={[
                      'flex flex-col items-center justify-center gap-1 rounded-2xl border-2 px-3 py-4 text-sm font-bold transition-colors active:scale-[0.99]',
                      selected
                        ? mode === 'OFICINA'
                          ? 'border-brand-500 bg-brand-light text-brand-700'
                          : 'border-info bg-blue-50 text-info'
                        : workModeMissing
                          ? 'border-critical/60 bg-surface text-ink-600'
                          : 'border-ink-200 bg-surface text-ink-600 hover:bg-ink-50',
                    ].join(' ')}
                  >
                    <span aria-hidden className="text-2xl">{mode === 'REMOTO' ? '🏠' : '🏢'}</span>
                    {mode === 'OFICINA' && name ? name : DECLARED_WORK_MODE_LABEL[mode]}
                    <span className="text-[11px] font-normal text-ink-400">{mode === 'REMOTO' ? 'Casa u otro lugar remoto' : name ? 'Mi oficina' : 'Sede u oficina de la empresa'}</span>
                  </button>
                );
              })}
            </div>
            {workModeMissing && !workMode && <p className="mt-2 text-xs font-medium text-critical">Elige una opción para poder marcar.</p>}
            {changingMode && <button type="button" onClick={() => setChangingMode(false)} className="mt-3 text-xs font-semibold text-ink-500 hover:underline">Dejar la modalidad de hoy</button>}
          </Card>
          )}
        </div>

        <OvertimePrompt refreshKey={overtimeKey} />
        <JornadaCard day={day} receivedAt={dayReceivedAt} isSubmitting={isSubmitting} note={note} onNoteChange={setNote} onMark={requestMark} />

        <Card>
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2 text-sm font-semibold text-ink-800">
              Ubicación
              <InfoTip title="¿Cómo se valida mi ubicación?" text={labels.t('AYUDA_UBICACION')} />
            </p>
            {gps.status === 'ok' && <Badge tone="success">GPS obtenido (±{Math.round(gps.accuracy ?? 0)} m)</Badge>}
            {gps.status === 'denied' && (lastGpsMark ? <Badge tone="info">Marcaste con GPS desde tu celular</Badge> : <Badge tone="critical">Sin permiso en este dispositivo</Badge>)}
            {gps.status === 'error' && <Badge tone="warning">Sin ubicación</Badge>}
            {gps.status === 'requesting' && <Badge tone="info">Solicitando…</Badge>}
          </div>
          {gps.status === 'denied' && lastGpsMark ? (
            <p className="mt-2 text-xs text-ink-500">
              Última marcación con GPS desde tu celular a las {new Date(lastGpsMark.at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', timeZone: day?.timeZone })}. Este dispositivo no tiene permiso de ubicación; si vas a marcar desde aquí, actívalo.
            </p>
          ) : (
            gps.message && <p className="mt-2 text-xs text-ink-400">{gps.message}</p>
          )}
          {gps.status !== 'ok' && gps.status !== 'requesting' && (
            <Button variant="outline" size="md" className="mt-3" onClick={requestGps}>
              Reintentar ubicación
            </Button>
          )}
          {locationMessage && (
            <p className={['mt-3 rounded-xl p-3 text-sm break-words', locationTone === 'ok' ? 'bg-green-50 text-green-800' : locationTone === 'out' ? 'bg-orange-50 text-orange-800' : 'bg-ink-50 text-ink-600'].join(' ')}>{locationMessage}</p>
          )}
          {gpsOk && (
            <button
              type="button"
              onClick={() => setMapOpen(true)}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-brand-600/30 active:scale-[0.99]"
            >
              <span aria-hidden>📍</span> Ver mi ubicación en el mapa
            </button>
          )}
        </Card>

        <Card>
          <p className="text-sm font-semibold text-ink-800">Mi ubicación de referencia</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {net?.home ? <Badge tone={net.home.status === 'APROBADA' ? 'success' : net.home.status === 'PENDIENTE' ? 'warning' : 'critical'}>Remoto {net.home.status.toLowerCase()}</Badge> : <Badge tone="neutral">Remoto sin registrar</Badge>}
            {net?.ownOffice ? <Badge tone={net.ownOffice.status === 'APROBADA' ? 'success' : net.ownOffice.status === 'PENDIENTE' ? 'warning' : 'critical'}>Oficina {net.ownOffice.status.toLowerCase()}</Badge> : <Badge tone="neutral">Oficina sin registrar</Badge>}
          </div>
          {netError && <p className="mt-2 text-xs text-ink-400">No se pudo consultar tu ubicación registrada.</p>}
          <Button variant="outline" size="md" className="mt-3" onClick={openRegister}>
            Registrar mi ubicación
          </Button>
        </Card>

        {lastResult && (
          <Card>
            <p className="text-sm font-semibold text-ink-800">Último marcaje</p>
            <div className="mt-2 flex items-center gap-2">
              <Badge tone="neutral">{RECORD_TYPE_LABEL[lastResult.recordType]}</Badge>
              <Badge tone={lastResult.modalityCode === 'PRESENCIAL' ? 'success' : lastResult.modalityCode === 'REMOTO' ? 'info' : 'warning'}>{lastResult.modalityName}</Badge>
              <span className="text-xs text-ink-400">{new Date(lastResult.recordTimestamp).toLocaleString('es-CO')}</span>
            </div>
            {lastResult.modalityReason && <p className="mt-2 text-xs text-ink-500">{lastResult.modalityReason}</p>}
            {driftExceeded && driftSeconds !== null && (
              <p className="mt-2 rounded-xl bg-amber-50 p-3 text-xs text-warning">
                La hora de tu celular está {formatMinutes(driftSeconds / 60)} {driftSeconds > 0 ? 'adelantada' : 'atrasada'} frente a la del servidor. El sistema usa siempre la hora del servidor; activa la hora automática en tu celular para evitar diferencias.
              </p>
            )}
            {lastResult.validations && lastResult.validations.length > 0 && (
              <div className="mt-3">
                <ValidationChecklist checks={lastResult.validations} />
              </div>
            )}
          </Card>
        )}

        <LocationMapSheet open={mapOpen} onClose={() => setMapOpen(false)} places={mapPlaces} user={mapUser} summary={locationMessage || 'Tu posición actual'} tone={locationTone} />

        <Modal open={!!firstAsk} onClose={() => { setFirstAsk(null); }} title={isOffice ? 'Registrar tu oficina' : 'Registrar tu lugar remoto'}>
          <div className="space-y-4">
            <p className="text-sm text-ink-700">
              {isOffice
                ? 'No hay una oficina registrada que valide este lugar. ¿Estás en la oficina y quieres registrarla? Si tu líder la aprueba, tus siguientes marcaciones se validarán contra ella.'
                : 'Aún no tienes un lugar remoto registrado. ¿Estás en tu lugar de trabajo remoto y quieres registrarlo? Si tu líder lo aprueba, tus siguientes marcaciones se validarán contra él.'}
            </p>
            <Button fullWidth onClick={() => saveFirstReference()} isLoading={isSavingFirst}>{isOffice ? 'Sí, registrar esta oficina' : 'Sí, registrar este lugar remoto'}</Button>
            {ctx?.requireApproval && <p className="text-xs text-ink-400">Quedará pendiente de aprobación del líder; hasta entonces tu marcación se registra y se audita.</p>}
            <Button variant="outline" fullWidth onClick={() => { const t = firstAsk?.type; setFirstSkipped(true); setFirstAsk(null); if (t) continueMark(t); }}>Ahora no, solo marcar</Button>
          </div>
        </Modal>

        <Modal open={!!deviationAsk} onClose={() => setDeviationAsk(null)} title="Confirma tu marcación">
          {deviationAsk && (
            <div className="space-y-4">
              <p className="text-sm text-ink-700">
                {deviationAsk.type === 'ENTRADA'
                  ? deviationAsk.minutes > 0
                    ? `Estás marcando tu entrada ${formatMinutes(deviationAsk.minutes)} tarde (tu horario empieza a las ${deviationAsk.target}).`
                    : `Estás marcando tu entrada ${formatMinutes(deviationAsk.minutes)} antes de tu hora (tu horario empieza a las ${deviationAsk.target}).`
                  : deviationAsk.minutes < 0
                    ? `Estás marcando tu salida ${formatMinutes(deviationAsk.minutes)} antes de tu hora (tu horario termina a las ${deviationAsk.target}).`
                    : `Estás marcando tu salida ${formatMinutes(deviationAsk.minutes)} después de tu hora (tu horario termina a las ${deviationAsk.target}).`}
              </p>
              <p className="text-xs text-ink-400">La marcación se guarda con la hora del servidor y esta diferencia queda registrada. Puedes agregar una nota si quieres explicarla.</p>
              <div className="flex gap-3">
                <Button variant="outline" fullWidth onClick={() => setDeviationAsk(null)}>Cancelar</Button>
                <Button fullWidth onClick={() => submit(deviationAsk.type, true)} isLoading={isSubmitting}>Confirmar</Button>
              </div>
            </div>
          )}
        </Modal>

        <Modal open={closeOpen} onClose={() => setCloseOpen(false)} title="Cerrar mi turno anterior">
          <div className="space-y-4">
            <p className="text-xs text-ink-500">{labels.t('AYUDA_CIERRE_TARDIO')}</p>
            {openShift?.marks && openShift.marks.length > 0 && (
              <div className="rounded-xl bg-ink-50 p-3 text-xs text-ink-600">
                <p className="mb-1 font-semibold text-ink-700">Marcaciones de ese turno</p>
                <ul className="space-y-0.5">
                  {openShift.marks.map((m) => (
                    <li key={`${m.type}-${m.at}`} className="flex justify-between gap-3">
                      <span>{RECORD_TYPE_LABEL[m.type]}</span>
                      <span className="tabular-nums">{shortDateTime(m.at, day?.timeZone)}</span>
                    </li>
                  ))}
                  <li className="flex justify-between gap-3 font-semibold text-critical">
                    <span>Salida</span>
                    <span>sin marcar</span>
                  </li>
                </ul>
                {openShift.scheduledEndAt && <p className="mt-2 text-ink-400">Tu salida programada era: {shortDateTime(openShift.scheduledEndAt, day?.timeZone)}</p>}
              </div>
            )}
            <Input
              label="Hora real de salida"
              type="datetime-local"
              value={closeAt}
              min={closeMin}
              max={toDatetimeLocal(new Date())}
              onChange={(e) => setCloseAt(e.target.value)}
              error={closeAtError ?? undefined}
              hint={closeAtError ? undefined : lastMarkAt ? `Debe ser posterior a ${shortDateTime(lastMarkAt.toISOString(), day?.timeZone)} (tu última marcación).` : undefined}
            />
            <Textarea label="¿Por qué quedó abierto? (obligatorio)" value={closeNote} maxLength={300} onChange={(e) => setCloseNote(e.target.value)} placeholder="Ej.: se me olvidó marcar la salida, terminé a las 5:30 p. m." hint={`Mínimo 5 caracteres · ${closeNote.trim().length}/300`} />
            <div className="flex gap-3">
              <Button variant="outline" fullWidth onClick={() => setCloseOpen(false)}>Cancelar</Button>
              <Button fullWidth onClick={closeOldShift} isLoading={isClosing} disabled={closeNote.trim().length < 5 || !closeAt || !!closeAtError}>Cerrar turno</Button>
            </div>
          </div>
        </Modal>

        <Modal open={homeDialog} onClose={() => setHomeDialog(false)} title="Registrar mi ubicación">
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-sm font-medium text-ink-700">¿Qué ubicación estás registrando ahora?</p>
              <div className="grid grid-cols-2 gap-3">
                {(['CASA', 'OFICINA'] as const).map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => setLocKind(kind)}
                    className={[
                      'rounded-xl border px-4 py-4 text-sm font-semibold transition-colors',
                      locKind === kind ? 'border-brand-500 bg-brand-light text-brand-700' : 'border-ink-200 bg-surface text-ink-600 hover:bg-ink-50',
                    ].join(' ')}
                  >
                    {kind === 'CASA' ? 'Mi lugar remoto' : 'Mi oficina'}
                    <span className="mt-1 block text-xs font-normal text-ink-400">{kind === 'CASA' ? 'Trabajo remoto' : 'Trabajo en oficina'}</span>
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-start gap-3 rounded-xl border border-ink-100 bg-surface p-3">
              <input type="checkbox" checked={mobileData} onChange={(e) => setMobileData(e.target.checked)} className="mt-0.5 h-5 w-5 rounded border-ink-300" />
              <span>
                <span className="block text-sm font-semibold text-ink-800">Estoy con los datos móviles del celular</span>
                <span className="block text-xs text-ink-400">Con datos móviles la IP es del operador y cambia; se guarda solo el GPS, sin la IP.</span>
              </span>
            </label>

            <div className="rounded-xl bg-ink-50 p-3 text-xs text-ink-500">
              <p className="font-semibold text-ink-700">Se guardará:</p>
              <p className="mt-1 break-all">Red: {mobileData ? 'no se guarda (solo GPS)' : net?.suggestedEntry ?? '—'}</p>
              <p>GPS: {gps.status === 'ok' ? `${gps.latitude!.toFixed(6)}, ${gps.longitude!.toFixed(6)} (±${Math.round(gps.accuracy ?? 0)} m)` : 'sin ubicación por ahora'}</p>
              <p className="mt-1">Hazlo solo si estás físicamente en ese lugar. Si ya habías registrado este tipo de ubicación, se reemplaza.</p>
            </div>
            {mobileData && gps.status !== 'ok' && <p className="text-xs text-critical">Para registrar solo con GPS activa la ubicación del dispositivo y pulsa "Reintentar ubicación".</p>}

            <div className="flex gap-3 pt-1">
              <Button variant="outline" fullWidth onClick={() => setHomeDialog(false)}>Cancelar</Button>
              <Button fullWidth onClick={registerHome} isLoading={isRegisteringHome} disabled={!locKind || (mobileData && gps.status !== 'ok')}>
                Registrar
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}
